/**
 * PaymentSelection Component
 *
 * Payment method selection: Stripe card or Whish Money.
 */

import React from 'react';

export type PaymentMethod = 'stripe' | 'whish_money';

interface PaymentSelectionProps {
  value: PaymentMethod;
  onChange: (method: PaymentMethod) => void;
  /** When the Whish Pay API is enabled, the guest is redirected to a hosted
   * payment page instead of following manual transfer instructions. */
  whishApiEnabled?: boolean;
}

export const PaymentSelection: React.FC<PaymentSelectionProps> = ({
  value,
  onChange,
  whishApiEnabled = false,
}) => {
  return (
    <div className="space-y-3">
      {/* Credit/Debit Card (Stripe) */}
      <label
        className={`block p-4 border rounded-xl cursor-pointer transition-all ${
          value === 'stripe'
            ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-start gap-3">
          <input
            type="radio"
            name="paymentMethod"
            value="stripe"
            checked={value === 'stripe'}
            onChange={() => onChange('stripe')}
            className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CreditCardIcon className="w-5 h-5 text-gray-700" />
              <span className="font-medium">Credit / Debit Card</span>
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                Recommended
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Pay securely with Visa, Mastercard, or AMEX via Stripe
            </p>
          </div>
        </div>
      </label>

      {/* Whish Money */}
      <label
        className={`block p-4 border rounded-xl cursor-pointer transition-all ${
          value === 'whish_money'
            ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-start gap-3">
          <input
            type="radio"
            name="paymentMethod"
            value="whish_money"
            checked={value === 'whish_money'}
            onChange={() => onChange('whish_money')}
            className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <PhoneIcon className="w-5 h-5 text-gray-700" />
              <span className="font-medium">Whish Money</span>
            </div>
            <p className="text-sm text-gray-600">
              {whishApiEnabled
                ? 'Pay instantly on the secure Whish payment page.'
                : 'Send a Whish transfer or pay cash at an agent. We show you how next.'}
            </p>
          </div>
        </div>
      </label>

      {/* Payment terms */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium mb-2 text-sm">Payment Terms</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li className="flex items-start gap-2">
            <CheckIcon className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>Payment is processed after the host approves your booking</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckIcon className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>You won't be charged until your booking is confirmed</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckIcon className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <span>All card payments are secured by Stripe</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

// Icons
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CreditCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const PhoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

export default PaymentSelection;
